import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import {
  fetchContacts,
  orderColumns,
  todayStamp,
  triggerCsvDownload,
} from "./exportContactsCsv";
import "./DataExportSettings.css";

/* =========================================================
 * Field metadata: friendly labels + category mapping
 * ======================================================= */

// Map raw field keys → friendly English labels.
// Anything not listed gets auto-humanized (e.g. cf_my_field → "My Field").
const FIELD_LABELS = {
  id: "Contact ID",

  // Contact info
  lead_email: "Email",
  lead_telephone: "Phone",
  lead_phone_whatsapp: "WhatsApp Phone",
  lead_firstname: "First Name",
  lead_lastname: "Last Name",
  business_name: "Company / Business Name",
  lead_company: "Company",
  lead_position: "Position",
  lead_title: "Title",
  lead_address: "Street Address",
  lead_city: "City",
  lead_postcode: "Postal Code",
  lead_country: "Country",
  lead_decision_makers: "Decision Makers",
  lead_referral_by: "Referred By",

  // Online presence
  lead_website: "Website",
  lead_instagram: "Instagram",
  lead_instagram_username: "Instagram Username",
  lead_instagram_id: "Instagram ID",
  lead_instagram_followers: "Instagram Followers",
  lead_instagram_category: "Instagram Category",
  lead_posts: "Instagram Posts",
  lead_linkedin: "LinkedIn",
  lead_tiktok: "TikTok",
  lead_facebook: "Facebook",
  lead_twitter: "Twitter / X",
  lead_youtube: "YouTube",
  lead_socials: "All Social Links",
  lead_link: "Link",
  lead_image: "Image URL",
  lead_mapsurl: "Google Maps Link",
  lead_description: "Description",
  lead_category: "Category",
  lead_type: "Type",
  lead_rating: "Rating",
  lead_reviews: "Reviews",
  lead_stars: "Stars",

  // Pipeline & status
  lead_status: "Pipeline Status",
  lead_contacted: "Contact Status",
  lead_previous_status: "Previous Status",
  lead_since: "Lead Since",
  lead_added_at: "Added On",
  flow_sort_priority: "Priority",

  // Outreach & email
  lead_last_email: "Last Email",
  lastEmailSent: "Last Sent Email",
  emailHistory: "Email History",
  outreachHistory: "Outreach History",
  lead_contacted_timestamp: "Last Contacted At",
  lead_contacted_days_ago: "Days Since Contact",
  lead_reply: "Last Reply",
  lead_last_reply_timestamp: "Last Reply Date",
  lead_template: "Email Template",
  lead_templates: "Templates by Stage",

  // Tags & flows
  tags: "Tags",
  touchpoints: "Touchpoints",
  activeFlow: "Active Flow",
  flowHistory: "Flow History",
  warmupFlow: "Warmup Flow",

  // Notes & files
  lead_notes: "Notes",
  lead_deal_value: "Deal Value",
  files: "Attached Files",
  profile_image: "Profile Image",

  // AI & enrichment
  leadSignals: "AI Signals",
  researchIntelligence: "Research Insights",
  lead_score: "Lead Score",
  spy_confidence_score: "Research Confidence",
  spy_last_updated: "Research Last Updated",
  lead_pacing: "Communication Pace",
  lead_tone_tags: "Tone Tags",
  lead_tags: "Auto Tags",
  lead_priority: "Auto Priority",
  lead_route: "Auto Route",
  resolved_company_id: "Matched Company ID",
  company_match_confidence: "Company Match Confidence",
  company_match_reason: "Company Match Reason",

  // Source & import
  source: "Source",
  import_name: "Import Name",
  import_source: "Import Source",
  import_mode: "Import Mode",
  imported_at: "Imported At",
  timestamp: "Created Timestamp",
  created_at: "Created At",
  updated_at: "Updated At",
  last_auto_import: "Last Auto Import",
  last_merged_at: "Last Merged At",
  reviewed_at: "Reviewed At",

  // Audit / trash
  trashedAt: "Trashed At",
  trashedBy: "Trashed By",
  trashedByEmail: "Trashed By (Email)",
  removedAt: "Removed At",
  removedBy: "Removed By",
  removedByEmail: "Removed By (Email)",
  removedByRole: "Removed By (Role)",
  restoredAt: "Restored At",
  restoredBy: "Restored By",
  restoredByEmail: "Restored By (Email)",
  restoredByRole: "Restored By (Role)",

  // Internal flags / dedup
  search_terms: "Search Index",
  business_name_normalized: "Normalized Business Name",
  lead_email_dedup: "Deduplication Key (Email)",
  business_name_dedup: "Deduplication Key (Business)",
  has_email: "Has Email",
  has_website: "Has Website",
  has_instagram: "Has Instagram",
  is_ready_for_spy: "Ready for Research",
};

// Map raw field keys → category id.
// Anything not mapped (incl. cf_*) falls back to autoDetectCategory().
const FIELD_CATEGORY = {
  id: "contact",

  lead_email: "contact",
  lead_telephone: "contact",
  lead_phone_whatsapp: "contact",
  lead_firstname: "contact",
  lead_lastname: "contact",
  business_name: "contact",
  lead_company: "contact",
  lead_position: "contact",
  lead_title: "contact",
  lead_address: "contact",
  lead_city: "contact",
  lead_postcode: "contact",
  lead_country: "contact",
  lead_decision_makers: "contact",
  lead_referral_by: "contact",

  lead_website: "online",
  lead_instagram: "online",
  lead_instagram_username: "online",
  lead_instagram_id: "online",
  lead_instagram_followers: "online",
  lead_instagram_category: "online",
  lead_posts: "online",
  lead_linkedin: "online",
  lead_tiktok: "online",
  lead_facebook: "online",
  lead_twitter: "online",
  lead_youtube: "online",
  lead_socials: "online",
  lead_link: "online",
  lead_image: "online",
  lead_mapsurl: "online",
  lead_description: "online",
  lead_category: "online",
  lead_type: "online",
  lead_rating: "online",
  lead_reviews: "online",
  lead_stars: "online",

  lead_status: "pipeline",
  lead_contacted: "pipeline",
  lead_previous_status: "pipeline",
  lead_since: "pipeline",
  lead_added_at: "pipeline",
  flow_sort_priority: "pipeline",

  lead_last_email: "outreach",
  lastEmailSent: "outreach",
  emailHistory: "outreach",
  outreachHistory: "outreach",
  lead_contacted_timestamp: "outreach",
  lead_contacted_days_ago: "outreach",
  lead_reply: "outreach",
  lead_last_reply_timestamp: "outreach",
  lead_template: "outreach",
  lead_templates: "outreach",

  tags: "tags",
  touchpoints: "tags",
  activeFlow: "tags",
  flowHistory: "tags",
  warmupFlow: "tags",

  lead_notes: "notes",
  lead_deal_value: "notes",
  files: "notes",
  profile_image: "notes",

  leadSignals: "ai",
  researchIntelligence: "ai",
  lead_score: "ai",
  spy_confidence_score: "ai",
  spy_last_updated: "ai",
  lead_pacing: "ai",
  lead_tone_tags: "ai",
  lead_tags: "ai",
  lead_priority: "ai",
  lead_route: "ai",
  resolved_company_id: "ai",
  company_match_confidence: "ai",
  company_match_reason: "ai",

  source: "source",
  import_name: "source",
  import_source: "source",
  import_mode: "source",
  imported_at: "source",
  timestamp: "source",
  created_at: "source",
  updated_at: "source",
  last_auto_import: "source",
  last_merged_at: "source",
  reviewed_at: "source",

  trashedAt: "audit",
  trashedBy: "audit",
  trashedByEmail: "audit",
  removedAt: "audit",
  removedBy: "audit",
  removedByEmail: "audit",
  removedByRole: "audit",
  restoredAt: "audit",
  restoredBy: "audit",
  restoredByEmail: "audit",
  restoredByRole: "audit",

  search_terms: "system",
  business_name_normalized: "system",
  lead_email_dedup: "system",
  business_name_dedup: "system",
  has_email: "system",
  has_website: "system",
  has_instagram: "system",
  is_ready_for_spy: "system",
};

const CATEGORY_DEFINITIONS = [
  { id: "contact", label: "Contact Info", description: "Name, email, phone, address, decision makers." },
  { id: "online", label: "Online Presence", description: "Website, social profiles, ratings, descriptions." },
  { id: "pipeline", label: "Pipeline & Status", description: "Lead stage, contact status, priority." },
  { id: "outreach", label: "Outreach & Email", description: "Email history, last contact, templates, replies." },
  { id: "tags", label: "Tags & Flows", description: "Tags, touchpoints, active and past flows." },
  { id: "notes", label: "Notes & Files", description: "Notes, deal value, attached files, profile image." },
  { id: "ai", label: "AI & Enrichment", description: "AI-detected signals, research insights, scoring." },
  { id: "source", label: "Source & Import", description: "Where each contact came from and when." },
  { id: "audit", label: "Audit & History", description: "Trashed/removed/restored events with user info." },
  { id: "custom", label: "Your Custom Fields", description: "Fields you created yourself." },
  { id: "system", label: "System Fields", description: "Internal helpers (dedup keys, search index)." },
  { id: "other", label: "Other Fields", description: "Other detected fields." },
];

// Default-OFF categories when user opens the picker.
// Everything else is on. We keep the list small so power users
// still see what they store, but the spreadsheet stays clean.
const CATEGORIES_OFF_BY_DEFAULT = new Set(["system", "audit"]);

/* =========================================================
 * Helpers
 * ======================================================= */

const humanizeKey = (key) => {
  if (!key) return "";
  const stripped = key
    .replace(/^cf_/, "")
    .replace(/^_+/, "")
    .replace(/_/g, " ")
    .trim();
  return stripped
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const labelFor = (key) => FIELD_LABELS[key] || humanizeKey(key);

const autoDetectCategory = (key) => {
  if (FIELD_CATEGORY[key]) return FIELD_CATEGORY[key];
  if (key.startsWith("cf_")) return "custom";
  if (key.startsWith("_")) return "system";
  return "other";
};

const groupKeysByCategory = (keys) => {
  const grouped = {};
  CATEGORY_DEFINITIONS.forEach((c) => { grouped[c.id] = []; });
  keys.forEach((k) => {
    const cat = autoDetectCategory(k);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(k);
  });
  Object.keys(grouped).forEach((cat) => {
    grouped[cat].sort((a, b) => labelFor(a).localeCompare(labelFor(b)));
  });
  return grouped;
};

/* =========================================================
 * Field Picker Modal
 * ======================================================= */

const FieldPickerModal = ({ kind, allKeys, onClose, onConfirm }) => {
  const grouped = useMemo(() => groupKeysByCategory(allKeys), [allKeys]);

  // Collapsed state per category
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    CATEGORY_DEFINITIONS.forEach((c) => {
      init[c.id] = CATEGORIES_OFF_BY_DEFAULT.has(c.id);
    });
    return init;
  });

  const [selected, setSelected] = useState(() => {
    const init = new Set();
    Object.entries(grouped).forEach(([cat, keys]) => {
      if (CATEGORIES_OFF_BY_DEFAULT.has(cat)) return;
      keys.forEach((k) => init.add(k));
    });
    if (allKeys.includes("id")) init.add("id");
    return init;
  });

  const [search, setSearch] = useState("");

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const visibleCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    return CATEGORY_DEFINITIONS.map((cat) => {
      const keys = grouped[cat.id] || [];
      if (keys.length === 0) return null;
      if (!term) return { ...cat, keys };
      const filtered = keys.filter((k) =>
        labelFor(k).toLowerCase().includes(term) || k.toLowerCase().includes(term)
      );
      if (filtered.length === 0) return null;
      return { ...cat, keys: filtered };
    }).filter(Boolean);
  }, [grouped, search]);

  const totalKeys = allKeys.length;
  const selectedCount = selected.size;

  const toggleField = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setCategory = (categoryId, checked) => {
    const keys = grouped[categoryId] || [];
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (checked) next.add(k);
        else next.delete(k);
      });
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allKeys));
  const clearAll = () => setSelected(new Set());
  const restoreDefaults = () => {
    const init = new Set();
    Object.entries(grouped).forEach(([cat, keys]) => {
      if (CATEGORIES_OFF_BY_DEFAULT.has(cat)) return;
      keys.forEach((k) => init.add(k));
    });
    if (allKeys.includes("id")) init.add("id");
    setSelected(init);
  };

  const toggleCollapse = (categoryId) => {
    setCollapsed((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleDownload = () => {
    const cols = Array.from(selected);
    if (cols.length === 0) return;
    onConfirm(cols);
  };

  const title = kind === "leads" ? "Customize Lead Export" : "Customize Database Export";

  return createPortal(
    <div className="dataExport-modal-overlay" onClick={onClose}>
      <div className="dataExport-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dataExport-modal-header">
          <div>
            <h3>{title}</h3>
            <p>Pick exactly the columns you want in your spreadsheet.</p>
          </div>
          <button className="dataExport-modal-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="dataExport-modal-toolbar">
          <div className="dataExport-modal-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search fields..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="dataExport-modal-quickActions">
            <button type="button" className="dataExport-pill" onClick={selectAll}>Select all</button>
            <button type="button" className="dataExport-pill" onClick={clearAll}>Clear all</button>
            <button type="button" className="dataExport-pill dataExport-pill--accent" onClick={restoreDefaults}>
              Recommended only
            </button>
          </div>
        </div>

        <div className="dataExport-modal-body">
          {visibleCategories.length === 0 && (
            <div className="dataExport-modal-empty">
              No fields match "{search}".
            </div>
          )}
          {visibleCategories.map((cat) => {
            const total = cat.keys.length;
            const sel = cat.keys.filter((k) => selected.has(k)).length;
            const allChecked = sel === total;
            const someChecked = sel > 0 && sel < total;
            return (
              <div key={cat.id} className={`dataExport-category ${collapsed[cat.id] ? "is-collapsed" : ""}`}>
                <div className="dataExport-category-head">
                  <label className="dataExport-category-toggle">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={(e) => setCategory(cat.id, e.target.checked)}
                    />
                    <span className="dataExport-category-title">
                      {cat.label}
                      <span className="dataExport-category-count">{sel} / {total}</span>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="dataExport-category-collapse"
                    onClick={() => toggleCollapse(cat.id)}
                    aria-label={collapsed[cat.id] ? "Expand" : "Collapse"}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
                {!collapsed[cat.id] && (
                  <div className="dataExport-category-body">
                    {cat.description && (
                      <p className="dataExport-category-description">{cat.description}</p>
                    )}
                    <div className="dataExport-field-list">
                      {cat.keys.map((key) => (
                        <label key={key} className="dataExport-field">
                          <input
                            type="checkbox"
                            checked={selected.has(key)}
                            onChange={() => toggleField(key)}
                          />
                          <span className="dataExport-field-label">{labelFor(key)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="dataExport-modal-footer">
          <span className="dataExport-modal-counter">
            {selectedCount} of {totalKeys} fields selected
          </span>
          <div className="dataExport-modal-actions">
            <button type="button" className="altBtn altBtn--petrol" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="altBtn altBtn--save"
              onClick={handleDownload}
              disabled={selectedCount === 0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download CSV ({selectedCount})</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* =========================================================
 * Main component
 * ======================================================= */

const DataExportSettings = ({ userId }) => {
  const [totalCount, setTotalCount] = useState(null);
  const [leadsCount, setLeadsCount] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);

  // Active export job — null when idle.
  // { kind: 'leads' | 'full', phase: 'fetching' | 'picking', rows, allKeys, processed, total }
  const [job, setJob] = useState(null);

  const [feedback, setFeedback] = useState({ type: null, message: "" });

  const fetchCounts = useCallback(async () => {
    if (!userId) {
      setCountsLoading(false);
      return;
    }
    setCountsLoading(true);
    try {
      const baseRef = collection(firestore, `World/${userId}/db`);
      const totalSnap = await getCountFromServer(baseRef);
      setTotalCount(totalSnap.data().count);
      const leadsSnap = await getCountFromServer(query(baseRef, where("lead_status", "==", "Lead")));
      setLeadsCount(leadsSnap.data().count);
    } catch (err) {
      console.error("[DataExport] count fetch failed:", err);
      setTotalCount(null);
      setLeadsCount(null);
    } finally {
      setCountsLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Internal: fetch contacts for an export and either go straight to download
  // (mode = 'all') or open the picker (mode = 'pick').
  const startJob = async (kind, mode) => {
    if (!userId || job) return;
    setFeedback({ type: null, message: "" });

    const filterLeadsOnly = kind === "leads";
    const totalEstimate = filterLeadsOnly ? leadsCount || 0 : totalCount || 0;

    setJob({
      kind,
      mode,
      phase: "fetching",
      rows: [],
      allKeys: [],
      processed: 0,
      total: totalEstimate,
    });

    try {
      const { rows, keys } = await fetchContacts({
        userId,
        filterLeadsOnly,
        onProgress: (n) => {
          setJob((prev) => prev && prev.kind === kind ? { ...prev, processed: n } : prev);
        },
      });

      if (rows.length === 0) {
        setJob(null);
        setFeedback({
          type: "info",
          message: filterLeadsOnly
            ? "No leads to export yet."
            : "Your contact database is empty — nothing to export.",
        });
        return;
      }

      if (mode === "all") {
        const cols = orderColumns(keys);
        const filename =
          kind === "leads"
            ? `leads_export_${todayStamp()}.csv`
            : `contacts_export_${todayStamp()}.csv`;
        triggerCsvDownload(rows, cols, filename);
        setJob(null);
        setFeedback({
          type: "success",
          message: `${rows.length.toLocaleString("en-US")} ${
            filterLeadsOnly ? "leads" : "contacts"
          } exported with ${cols.length} columns.`,
        });
      } else {
        // Mode 'pick' → open the field picker with cached rows.
        setJob({
          kind,
          mode,
          phase: "picking",
          rows,
          allKeys: keys,
          processed: rows.length,
          total: rows.length,
        });
      }
    } catch (err) {
      console.error("[DataExport] export failed:", err);
      setJob(null);
      setFeedback({
        type: "error",
        message: `Export failed: ${err.message || "Unknown error"}. Please try again.`,
      });
    }
  };

  const handlePickerConfirm = (selectedKeys) => {
    if (!job || !selectedKeys || selectedKeys.length === 0) return;
    const cols = orderColumns(selectedKeys);
    const filename =
      job.kind === "leads"
        ? `leads_export_custom_${todayStamp()}.csv`
        : `contacts_export_custom_${todayStamp()}.csv`;
    triggerCsvDownload(job.rows, cols, filename);
    const exportedKind = job.kind;
    const exportedCount = job.rows.length;
    setJob(null);
    setFeedback({
      type: "success",
      message: `${exportedCount.toLocaleString("en-US")} ${
        exportedKind === "leads" ? "leads" : "contacts"
      } exported with ${cols.length} columns.`,
    });
  };

  const handlePickerCancel = () => setJob(null);

  const renderProgress = (kind) => {
    if (!job || job.kind !== kind || job.phase !== "fetching") return null;
    const pct = job.total > 0
      ? Math.min(100, Math.round((job.processed / job.total) * 100))
      : 0;
    return (
      <div className="dataExport-progress">
        <div className="dataExport-progress-bar">
          <div className="dataExport-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="dataExport-progress-text">
          {job.processed.toLocaleString("en-US")}
          {job.total > 0 ? ` / ${job.total.toLocaleString("en-US")}` : ""}
          {" loaded…"}
        </span>
      </div>
    );
  };

  const formatCount = (val) => {
    if (countsLoading) return "…";
    if (val === null || val === undefined) return "–";
    return val.toLocaleString("en-US");
  };

  const isBusy = !!job;
  const isFetchingLeadsAll = job && job.kind === "leads" && job.mode === "all" && job.phase === "fetching";
  const isFetchingLeadsPick = job && job.kind === "leads" && job.mode === "pick" && job.phase === "fetching";
  const isFetchingFullAll = job && job.kind === "full" && job.mode === "all" && job.phase === "fetching";
  const isFetchingFullPick = job && job.kind === "full" && job.mode === "pick" && job.phase === "fetching";

  const exportCards = [
    {
      kind: "leads",
      icon: "fa-bullseye",
      eyebrow: "Pipeline",
      title: "Export Leads",
      desc: "A spreadsheet of every active lead — notes, tags, touchpoints, email history, decision makers and AI insights.",
      points: [
        "Only contacts currently in your sales pipeline",
        "Includes notes, tags, touchpoints and email history",
        "Decision makers and AI insights stay in the file",
      ],
      count: formatCount(leadsCount),
      countLabel: leadsCount === 1 ? "lead" : "leads",
      disabled: isBusy || countsLoading || !leadsCount,
      fetchingAll: isFetchingLeadsAll,
      fetchingPick: isFetchingLeadsPick,
    },
    {
      kind: "full",
      icon: "fa-database",
      eyebrow: "Everything",
      title: "Export Full Database",
      desc: "Every contact you have ever added — leads, replied, removed, archived. For backups, audits, or moving to another tool.",
      points: [
        "Every contact, regardless of pipeline stage",
        "Opens in Excel, Google Sheets and Numbers",
        "Accents and special characters stay intact",
      ],
      count: formatCount(totalCount),
      countLabel: totalCount === 1 ? "contact" : "contacts",
      disabled: isBusy || countsLoading || !totalCount,
      fetchingAll: isFetchingFullAll,
      fetchingPick: isFetchingFullPick,
    },
  ];

  return (
    <div className="de">
      <header className="de-page-head">
        <div>
          <h3 className="de-page-title">Data Export</h3>
          <p className="de-page-sub">
            Download your leads or the full contact database as a CSV. One click for everything,
            or pick the columns yourself.
          </p>
        </div>
        <span className="de-badge">
          {formatCount(totalCount)} {totalCount === 1 ? "contact" : "contacts"}
        </span>
      </header>

      <div className="de-cols">
        {exportCards.map((card) => (
          <div key={card.kind} className="de-card">
            <div className="de-card-top">
              <span className="de-icon" aria-hidden>
                <i className={`fa ${card.icon}`} />
              </span>
              <span className="de-card-count">
                {card.count} {card.countLabel}
              </span>
            </div>
            <div>
              <span className="de-card-eyebrow">{card.eyebrow}</span>
              <h4 className="de-card-title">{card.title}</h4>
            </div>
            <p className="de-card-desc">{card.desc}</p>
            <ul className="de-points">
              {card.points.map((p) => (
                <li key={p}><i className="fa fa-circle-check" />{p}</li>
              ))}
            </ul>
            <div className="de-card-actions">
              <button
                type="button"
                className={`altBtn altBtn--save ${card.fetchingAll ? "is-loading" : ""}`}
                onClick={() => startJob(card.kind, "all")}
                disabled={card.disabled}
              >
                {card.fetchingAll ? (
                  <>
                    <span className="dataExport-spinner" />
                    Preparing…
                  </>
                ) : (
                  <><i className="fa fa-download" /> Download CSV</>
                )}
              </button>
              <button
                type="button"
                className={`altBtn altBtn--petrol ${card.fetchingPick ? "is-loading" : ""}`}
                onClick={() => startJob(card.kind, "pick")}
                disabled={card.disabled}
              >
                {card.fetchingPick ? (
                  <>
                    <span className="dataExport-spinner dataExport-spinner--ghost" />
                    Loading fields…
                  </>
                ) : (
                  "Choose fields"
                )}
              </button>
            </div>
            {renderProgress(card.kind)}
          </div>
        ))}
      </div>

      {feedback.message && (
        <div className={`de-feedback de-feedback--${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      <div className="de-note">
        <span className="de-icon de-note-icon" aria-hidden>
          <i className="fa fa-circle-info" />
        </span>
        <span>
          Exported files contain personal data — handle them in line with your privacy obligations (e.g. GDPR).
          Complex fields like email history stay as structured text in a single cell, so nothing is lost.
        </span>
      </div>

      {job && job.phase === "picking" && (
        <FieldPickerModal
          kind={job.kind}
          allKeys={job.allKeys}
          onClose={handlePickerCancel}
          onConfirm={handlePickerConfirm}
        />
      )}
    </div>
  );
};

export default DataExportSettings;
